import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash2, Edit2, Loader2, X } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [u, d] = await Promise.all([api.get("/users"), api.get("/dealers")]);
    setUsers(u.data); setDealers(d.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (u) => {
    if (!confirm(`Benutzer "${u.email}" wirklich löschen?`)) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  return (
    <div className="p-10 max-w-6xl" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Administration</div>
          <h1 className="text-3xl font-semibold tracking-tight">Benutzer</h1>
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="add-user-button"
                className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center gap-2">
          <Plus size={14}/> Neuer Benutzer
        </button>
      </div>

      <div className="border border-zinc-200">
        {loading ? <div className="p-8 text-center text-sm text-zinc-500">Wird geladen...</div> : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">E-Mail</th>
                <th className="text-left px-5 py-2.5 font-medium">Name</th>
                <th className="text-left px-5 py-2.5 font-medium">Rolle</th>
                <th className="text-left px-5 py-2.5 font-medium">Händler</th>
                <th className="text-left px-5 py-2.5 font-medium">Aktiv</th>
                <th className="text-right px-5 py-2.5 font-medium w-24">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-5 py-3">{u.name}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                      u.role === "admin" ? "bg-zinc-900 text-white border-zinc-900" : "bg-zinc-100 text-zinc-700 border-zinc-200"
                    }`}>{u.role}</span>
                  </td>
                  <td className="px-5 py-3 text-zinc-600">{dealers.find((d) => d.id === u.dealerId)?.name || "—"}</td>
                  <td className="px-5 py-3 text-zinc-600">{u.active ? "Ja" : "Nein"}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => { setEditing(u); setShowModal(true); }} className="text-zinc-500 hover:text-zinc-900 mr-3" data-testid={`edit-user-${u.email}`}>
                      <Edit2 size={14}/>
                    </button>
                    <button onClick={() => onDelete(u)} className="text-zinc-400 hover:text-red-600" data-testid={`delete-user-${u.email}`}>
                      <Trash2 size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && <UserModal user={editing} dealers={dealers} onClose={() => setShowModal(false)} onSaved={load} />}
    </div>
  );
}

function UserModal({ user, dealers, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    role: user?.role || "dealer",
    dealerId: user?.dealerId || "",
    password: "",
    active: user?.active ?? true,
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      if (user) {
        const payload = { name: form.name, role: form.role, active: form.active, dealerId: form.dealerId || null };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${user.id}`, payload);
      } else {
        await api.post("/users", {
          email: form.email, password: form.password, name: form.name,
          role: form.role, dealerId: form.dealerId || null,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="user-modal">
      <div className="bg-white border border-zinc-200 max-w-md w-full">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <h3 className="font-semibold tracking-tight">{user ? "Benutzer bearbeiten" : "Neuer Benutzer"}</h3>
          <button onClick={onClose}><X size={16} className="text-zinc-500"/></button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <Field label="E-Mail">
            <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required disabled={!!user}
                   data-testid="user-form-email"
                   className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm disabled:bg-zinc-50"/>
          </Field>
          <Field label="Name">
            <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="user-form-name"
                   className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm"/>
          </Field>
          <Field label={user ? "Neues Passwort (optional)" : "Passwort"}>
            <input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                   required={!user} data-testid="user-form-password"
                   className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm"/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Rolle">
              <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="user-form-role"
                      className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
                <option value="dealer">Händler</option>
                <option value="admin">Administrator</option>
              </select>
            </Field>
            <Field label="Aktiv">
              <select value={form.active ? "1" : "0"} onChange={(e) => setForm({...form, active: e.target.value === "1"})}
                      className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
                <option value="1">Ja</option><option value="0">Nein</option>
              </select>
            </Field>
          </div>
          {form.role === "dealer" && (
            <Field label="Händler">
              <select value={form.dealerId} onChange={(e) => setForm({...form, dealerId: e.target.value})} data-testid="user-form-dealer"
                      className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
                <option value="">— Keine Zuordnung —</option>
                {dealers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
          )}
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-50">Abbrechen</button>
            <button type="submit" disabled={saving} data-testid="user-form-submit"
                    className="bg-zinc-900 text-white text-sm font-medium px-4 py-2 hover:bg-zinc-800 flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin"/>} Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
